import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem, Paper, Radio, RadioGroup, Select,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { bonusTrackerAPI } from '../../services/api';
import { AdjustmentInfo, RECON_LABEL, SettlementRow, errText, money, needsAttention } from './bonusTypes';

interface Props {
  isSuperUser: boolean;
  /** Accounting (or a super user) closes an overpayment. */
  canClose: boolean;
  onOpenBonus: (recordId: string) => void;
  onChanged?: () => void;
}

const STATE_LABEL = { SENT: 'Sent, not paid yet', SETTLED: 'Paid', NOT_SENT: 'Not sent' } as const;
const ADJ_LABEL: Record<AdjustmentInfo['status'], string> = { OPEN: 'To recover', ADDED: 'In a bonus', WITHDRAWN: 'Withdrawn', RECOVERED: 'Recovered', WAIVED: 'Waived' };
const adjColor = (s: AdjustmentInfo['status']): 'default' | 'warning' | 'success' | 'info' => (s === 'OPEN' ? 'warning' : s === 'RECOVERED' ? 'success' : s === 'ADDED' ? 'info' : 'default');
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/** The next Saturday-like week end is chosen by the person; this only suggests the coming Saturday. */
function nextSaturday(): string {
  const now = new Date();
  const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  while (d.getUTCDay() !== 6) d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

const SettlementPanel: React.FC<Props> = ({ isSuperUser, canClose, onOpenBonus, onChanged }) => {
  const [rows, setRows] = useState<SettlementRow[]>([]);
  const [adjustments, setAdjustments] = useState<AdjustmentInfo[]>([]);
  const [state, setState] = useState('WAITING');
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [adjusting, setAdjusting] = useState<SettlementRow | null>(null);
  const [closing, setClosing] = useState<AdjustmentInfo | null>(null);

  const load = useCallback(async () => {
    try {
      const [r, a] = await Promise.all([bonusTrackerAPI.settlement(state), bonusTrackerAPI.adjustments('ALL')]);
      setRows(r);
      setAdjustments(a);
    } catch (e) { setMessage({ type: 'error', text: errText(e) }); }
  }, [state]);
  useEffect(() => { void load(); }, [load]);

  const check = async () => {
    setBusy(true);
    try {
      const r = await bonusTrackerAPI.syncSettlement();
      if (!r.configured) setMessage({ type: 'info', text: 'Payroll is not connected on this server (TOGO_OPS_API_URL and BONUS_PORTAL_API_KEY).' });
      else if (!r.reachable) setMessage({ type: 'error', text: 'Payroll could not be reached. Nothing was changed. Try again in a moment.' });
      else setMessage({ type: r.issues ? 'error' : 'success', text: `Checked ${r.checked} earning(s) with payroll: ${r.newlySettled} newly paid, ${r.issues} problem(s) found.` });
      await load();
      onChanged?.();
    } catch (e) { setMessage({ type: 'error', text: errText(e) }); } finally { setBusy(false); }
  };

  const attention = rows.filter((r) => needsAttention(r.reconStatus)).length;

  return (
    <Box>
      {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          An earning is <b>paid</b> only when payroll has paid the period that includes it. Until then it is only <b>sent to payroll</b>. This list checks what TEKS TIME sent against what payroll recorded,
          so a missing, cancelled or different amount is seen at once. Nothing here changes payroll.
        </Typography>
        <Button variant="contained" sx={{ flexShrink: 0 }} onClick={() => void check()} disabled={busy}>Check with payroll now</Button>
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Show</InputLabel>
          <Select label="Show" value={state} onChange={(e) => setState(e.target.value)}>
            <MenuItem value="WAITING">Sent, waiting for payroll to pay</MenuItem>
            <MenuItem value="ATTENTION">Needs attention (does not match)</MenuItem>
            <MenuItem value="SETTLED">Paid</MenuItem>
            <MenuItem value="ALL">All sent to payroll</MenuItem>
          </Select>
        </FormControl>
        {attention > 0 && state !== 'ATTENTION' && <Chip color="error" size="small" label={`${attention} need attention`} />}
      </Stack>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell><TableCell>Bonus</TableCell><TableCell align="right">Amount</TableCell><TableCell>Payroll</TableCell><TableCell>Payroll check</TableCell><TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.lineId} hover>
                <TableCell>{r.employeeName}{r.employeeNumber ? ` (${r.employeeNumber})` : ''}</TableCell>
                <TableCell>
                  {r.programName}{r.isAdjustment && <Chip size="small" sx={{ ml: 1 }} label="Adjustment" />}
                  <Typography variant="caption" display="block" color="text.secondary">Ends {r.periodEnd}{r.reference ? ` · ${r.reference}` : ''}</Typography>
                </TableCell>
                <TableCell align="right">{money(r.amount, r.currency)}</TableCell>
                <TableCell>
                  {r.paymentState === 'SETTLED'
                    ? <Chip size="small" color="success" label={`Paid ${r.settledAt || ''}`} />
                    : <Chip size="small" color="info" label={STATE_LABEL[r.paymentState]} />}
                  <Typography variant="caption" display="block" color="text.secondary">
                    {r.settlementRef || r.payrollRef || ''}{r.paymentState === 'SENT' && r.daysWaiting > 0 ? ` · waiting ${r.daysWaiting} day(s)` : ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  {r.reconStatus ? (
                    <Tooltip title={r.reconNote || ''}>
                      <Chip size="small" variant="outlined" color={needsAttention(r.reconStatus) ? 'error' : 'default'} label={RECON_LABEL[r.reconStatus]} />
                    </Tooltip>
                  ) : <Typography variant="caption" color="text.secondary">not checked yet</Typography>}
                  {r.reconNote && needsAttention(r.reconStatus) && <Typography variant="caption" display="block" color="error">{r.reconNote}</Typography>}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => onOpenBonus(r.recordId)}>Open bonus</Button>
                  {isSuperUser && !r.isAdjustment && <Button size="small" onClick={() => setAdjusting(r)}>Adjust</Button>}
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={6}><Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>Nothing here.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Adjustments</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        A bonus sent to payroll cannot be edited. A super user adjusts it instead: an <b>extra payment</b> becomes a new earning that goes through every approval again (it may be above the program limits, with a reason),
        and an <b>overpayment</b> is tracked until accounting closes it as recovered or waived. Payroll is never changed automatically.
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Adjustment</TableCell><TableCell>Employee</TableCell><TableCell align="right">Amount</TableCell><TableCell>Reason</TableCell><TableCell>State</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {adjustments.map((a) => (
              <TableRow key={a.id} hover>
                <TableCell>{a.code}<Typography variant="caption" display="block" color="text.secondary">{a.kind === 'ADDITIONAL' ? 'Extra payment' : 'Overpayment'} · of {a.programName}{a.originalReference ? ` (${a.originalReference})` : ''}</Typography></TableCell>
                <TableCell>{a.employeeName}</TableCell>
                <TableCell align="right">{money(a.amount, a.currency)}</TableCell>
                <TableCell sx={{ maxWidth: 280 }}>{a.reason}{a.resolutionNote && <Typography variant="caption" display="block" color="text.secondary">{a.resolvedByName}: {a.resolutionNote}</Typography>}</TableCell>
                <TableCell><Chip size="small" color={adjColor(a.status)} label={ADJ_LABEL[a.status]} /></TableCell>
                <TableCell align="right">
                  {a.newRecordId && <Button size="small" onClick={() => onOpenBonus(a.newRecordId!)}>Open bonus</Button>}
                  {a.kind === 'OVERPAYMENT' && a.status === 'OPEN' && canClose && <Button size="small" onClick={() => setClosing(a)}>Close</Button>}
                </TableCell>
              </TableRow>
            ))}
            {!adjustments.length && <TableRow><TableCell colSpan={6}><Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No adjustments.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>

      {adjusting && <AdjustDialog row={adjusting} onClose={() => setAdjusting(null)} onSaved={(text) => { setAdjusting(null); setMessage({ type: 'success', text }); void load(); onChanged?.(); }} />}
      {closing && <CloseDialog adj={closing} onClose={() => setClosing(null)} onSaved={() => { setClosing(null); setMessage({ type: 'success', text: 'Overpayment closed.' }); void load(); }} />}
    </Box>
  );
};

const AdjustDialog: React.FC<{ row: SettlementRow; onClose: () => void; onSaved: (text: string) => void }> = ({ row, onClose, onSaved }) => {
  const [kind, setKind] = useState<'ADDITIONAL' | 'OVERPAYMENT'>('ADDITIONAL');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [periodEnd, setPeriodEnd] = useState(nextSaturday());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await bonusTrackerAPI.adjustLine(row.lineId, { kind, amount: Number(amount), reason, periodEnd: kind === 'ADDITIONAL' ? periodEnd : undefined });
      onSaved(kind === 'ADDITIONAL' ? 'Extra payment added to a bonus. It needs every approval before payroll can pay it.' : 'Overpayment recorded. Accounting closes it when it is recovered or waived.');
    } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Adjust {row.employeeName}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '16px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Typography variant="body2">{row.programName} · ends {row.periodEnd}{row.reference ? ` · ${row.reference}` : ''} · {money(row.amount, row.currency)} · {STATE_LABEL[row.paymentState].toLowerCase()}</Typography>
        <RadioGroup value={kind} onChange={(e) => setKind(e.target.value as 'ADDITIONAL' | 'OVERPAYMENT')}>
          <FormControlLabel value="ADDITIONAL" control={<Radio />} label="Extra payment owed (a new earning, approved again; may be above the program limits)" />
          <FormControlLabel value="OVERPAYMENT" control={<Radio />} label="Overpayment to recover (only for an earning payroll has paid)" disabled={row.paymentState !== 'SETTLED'} />
        </RadioGroup>
        <TextField size="small" required type="number" label={`Amount (${row.currency})`} value={amount} onChange={(e) => setAmount(e.target.value)} helperText={kind === 'OVERPAYMENT' ? `At most ${round2(row.amount).toFixed(2)}.` : undefined} />
        {kind === 'ADDITIONAL' && <TextField size="small" required type="date" label="Add it to the bonus ending" InputLabelProps={{ shrink: true }} value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} helperText="The last day of the program week." />}
        <TextField size="small" required multiline minRows={2} label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !(Number(amount) > 0) || reason.trim().length < 5 || (kind === 'ADDITIONAL' && !periodEnd)}>Save adjustment</Button>
      </DialogActions>
    </Dialog>
  );
};

const CloseDialog: React.FC<{ adj: AdjustmentInfo; onClose: () => void; onSaved: () => void }> = ({ adj, onClose, onSaved }) => {
  const [resolution, setResolution] = useState<'RECOVERED' | 'WAIVED'>('RECOVERED');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    try { await bonusTrackerAPI.closeAdjustment(adj.id, resolution, note); onSaved(); } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Close {adj.code}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '16px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Typography variant="body2">{adj.employeeName} · {money(adj.amount, adj.currency)} · {adj.reason}</Typography>
        <RadioGroup value={resolution} onChange={(e) => setResolution(e.target.value as 'RECOVERED' | 'WAIVED')}>
          <FormControlLabel value="RECOVERED" control={<Radio />} label="Recovered (say how, for example the payroll period it was deducted in)" />
          <FormControlLabel value="WAIVED" control={<Radio />} label="Waived (say why it is not recovered)" />
        </RadioGroup>
        <TextField size="small" required multiline minRows={2} label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || note.trim().length < 3}>Close adjustment</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettlementPanel;
